// src/pages/TicketScan/QrScanSessionPage.tsx
// Mục 8.3 (README_UPDATE.md) + README_QR_SCAN_PERFORMANCE.md — Giao vé / Thu hồi vé bằng cách ĐỌC MÃ
// QR QUA ẢNH. Component dùng CHUNG cho cả 2 luồng (phân biệt bằng prop scanType):
//   Luồng: mở phiên quét → chọn/chụp ảnh vé → giải mã QR ở client (html5-qrcode) → gửi token lên
//   /decode-batch (server giải mã token AES, không đụng DB) → FE đối chiếu số vé với tập dải hợp lệ
//   (server trả lúc mở phiên) → đẩy batch vào hàng đợi → "Xác nhận" = finalize (server gộp số rời
//   thành dải rồi gọi luồng CreateTicketDelivery + Confirm / CreateTicketRecall hiện có).
//
// Nguồn quét hỗ trợ (dùng song song được): (1) CAMERA thiết bị, (2) CHỌN/CHỤP ẢNH vé,
// (3) THIẾT BỊ QUÉT cầm tay (Honeywell...) qua 1 trong 2 kiểu kết nối:
//     - Bàn phím USB (HID): máy quét gõ nội dung mã QR rồi tự bấm Enter — FE bắt Enter trên ô nhập riêng.
//     - Cổng COM (USB Serial/RS-232): FE đọc trực tiếp cổng nối tiếp qua Web Serial API (hook useSerialScanner).
//     Cả 2 kiểu đều đẩy nội dung mã vào chung luồng xử lý với camera (handleDecodedText).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { Html5Qrcode } from 'html5-qrcode';
import { stopHtml5QrcodeSafely } from 'helpers/html5QrcodeCamera';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DataTableShell from 'Components/Common/DataTableShell';
import {
  cancelTicketScanSessionApi,
  decodeTicketToken,
  finalizeTicketScanSessionApi,
  getIssuancePlans,
  getTicketScanSessionApi,
  ingestTicketScanEntriesApi,
  openTicketScanSessionApi,
} from 'helpers/fakebackend_helper';
import { decodeScannedTicket } from 'helpers/ticketQrDecode';
import { beepDuplicate, beepOk, beepReject, primeScanAudio } from 'helpers/scanFeedback';
import { loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { IssuancePlan } from 'pages/IssuancePlan/types';
import { useBrandingTitle, useSerialScanner } from '../../hooks';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_scan');

// TicketScanTypes bên ticket-service: 1 = Giao vé, 2 = Thu hồi vé ế.
export type ScanType = 1 | 2;

interface Props {
  scanType: ScanType;
}

interface ScanRange { symbol: string; start: number; end: number }
interface ScanRow {
  key: string;
  seq: number; // số thứ tự cố định theo lúc thêm — không đổi khi danh sách dài thêm (giữ được React.memo)
  symbol: string;
  ticketNumber: string;
  ok: boolean;
  reason?: string;
  scannedAt: string;
}

const unwrap = (res: any) => res?.data ?? res;
const getItems = (res: any): any[] => unwrap(res)?.items ?? unwrap(res) ?? [];

// Text khác nhau giữa 2 luồng — gom 1 chỗ.
const CONFIG: Record<ScanType, {
  title: string;
  breadcrumbParent: string;
  agentLabel: string;
  openTitle: string;
  notePlaceholder: string;
  confirmLabel: string;
  successPrefix: string;
  confirmFailMsg: string;
  invalidRangeReason: string;
}> = {
  1: {
    title: st('giao_ve_bang_qr_anh_title'),
    breadcrumbParent: st('giao_ve_dai_ly_text'),
    agentLabel: st('dai_ly_nhan_ve_text'),
    openTitle: st('mo_phien_giao_ve_text'),
    notePlaceholder: st('ghi_chu_phieu_giao_ve_tuy_text'),
    confirmLabel: st('xac_nhan_giao_ve_text'),
    successPrefix: st('da_tao_phieu_giao_ve_text'),
    confirmFailMsg: st('xac_nhan_giao_ve_that_bai_text'),
    invalidRangeReason: st('ve_khong_thuoc_dai_con_trong_text'),
  },
  2: {
    title: st('thu_hoi_ve_bang_qr_anh_title'),
    breadcrumbParent: st('thu_hoi_ve_e_text'),
    agentLabel: st('dai_ly_bi_thu_hoi_text'),
    openTitle: st('mo_phien_thu_hoi_text'),
    notePlaceholder: st('ghi_chu_phieu_thu_hoi_tuy_text'),
    confirmLabel: st('xac_nhan_thu_hoi_text'),
    successPrefix: st('da_tao_phieu_thu_hoi_text'),
    confirmFailMsg: st('xac_nhan_thu_hoi_that_bai_text'),
    invalidRangeReason: st('ve_khong_thuoc_dai_da_giao_text'),
  },
};

// Khoá lưu lựa chọn bật/tắt thiết bị quét cầm tay (Honeywell...) giữa các lần vào trang.
const DEVICE_SCAN_PREF_KEY = 'qrScanSession.deviceScanOn';
// Khoá lưu kiểu thiết bị quét: 'keyboard' = bàn phím USB (HID), 'serial' = cổng COM (USB Serial).
const DEVICE_MODE_PREF_KEY = 'qrScanSession.deviceMode';
// Khoá lưu tốc độ baud cổng COM (một số máy quét Serial mặc định khác 9600).
const SERIAL_BAUD_PREF_KEY = 'qrScanSession.serialBaud';
const SERIAL_BAUD_OPTIONS = [9600, 19200, 38400, 57600, 115200];
type DeviceScanMode = 'keyboard' | 'serial';

// Bỏ qua đọc LẶP CÙNG 1 mã trong khoảng thời gian này (ms) — CAMERA đọc nhiều khung/giây nên cần dài.
const SCAN_COOLDOWN_MS = 2500;
// THIẾT BỊ QUÉT (serial/bàn phím): mỗi lần bóp cò = 1 sự kiện, không có khung lặp -> chỉ cần chặn
// double-trigger vô tình, cooldown ngắn để quét liên tục vẫn mượt.
const DEVICE_COOLDOWN_MS = 600;
// Kiểu bàn phím: coi là hết 1 lần quét khi .value ngừng đổi trong khoảng này (ms). Máy quét gõ mỗi
// ký tự cách nhau vài ms nên 120ms là quá đủ, người gõ tay thì không thể nhanh vậy.
const DEVICE_INPUT_DEBOUNCE_MS = 120;
// Dọn bớt map cooldown khi vượt ngưỡng để không phình bộ nhớ trong phiên quét dài.
const SCAN_COOLDOWN_MAX_ENTRIES = 300;
// Chỉ render tối đa số dòng gần nhất để bảng không giật khi phiên có hàng nghìn vé.
const MAX_VISIBLE_ROWS = 300;

// Dòng kết quả đối chiếu — tách riêng + React.memo để mỗi lần quét chỉ render dòng mới, không dựng
// lại toàn bộ bảng (danh sách có thể lên hàng nghìn vé).
interface ScanResultRowProps {
  index: number;
  row: ScanRow;
  flushed: boolean;
}
const ScanResultRow = React.memo<ScanResultRowProps>(({ index, row, flushed }) => (
  <tr className={row.ok ? '' : 'table-danger'}>
    <td className="text-center">{index}</td>
    <td>{row.symbol}</td>
    <td className="fw-semibold">{row.ticketNumber}</td>
    <td>
      {row.ok ? (
        <span className="text-success">
          <i className="ri-checkbox-circle-line me-1" />{st('hop_le_text')}
          {flushed && <span className="text-muted ms-2 fs-11">{st('da_day_text')}</span>}
        </span>
      ) : (
        <span className="text-danger"><i className="ri-close-circle-line me-1" />{row.reason}</span>
      )}
    </td>
    <td className="text-muted fs-12">{new Date(row.scannedAt).toLocaleTimeString('vi-VN')}</td>
  </tr>
));
ScanResultRow.displayName = 'ScanResultRow';

const QrScanSessionPage: React.FC<Props> = ({ scanType }) => {
  const cfg = CONFIG[scanType];
  useBrandingTitle(cfg.title);

  const readerElementId = useMemo(() => `qr-scan-reader-${scanType}`, [scanType]);
  const cameraElementId = useMemo(() => `qr-scan-camera-${scanType}`, [scanType]);

  const [agents, setAgents] = useState<IAgent[]>([]);
  const [plans, setPlans] = useState<IssuancePlan[]>([]);
  const [agentId, setAgentId] = useState('');
  const [planId, setPlanId] = useState('');

  const [session, setSession] = useState<any>(null);
  // Chưa chọn kỳ: vào chế độ "chờ vé đầu tiên" — kỳ lấy theo vé quét đầu tiên rồi mới mở phiên.
  const [preSession, setPreSession] = useState(false);
  const [ranges, setRanges] = useState<ScanRange[]>([]);
  const [rows, setRows] = useState<ScanRow[]>([]);
  const [opening, setOpening] = useState(false);
  const openingSessionRef = useRef(false);
  const [decoding, setDecoding] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [note, setNote] = useState('');

  // Thiết bị quét cầm tay (Honeywell...) — 2 kiểu kết nối: bàn phím USB (HID) gõ mã rồi tự Enter,
  // hoặc cổng COM (USB Serial) đọc qua Web Serial API. Dùng song song được với camera / chọn ảnh.
  // Lựa chọn bật/tắt + kiểu kết nối được nhớ qua localStorage.
  //
  // Ô nhập kiểu bàn phím để KHÔNG kiểm soát bằng state (uncontrolled): máy quét "gõ" cực nhanh, nếu
  // đọc từ React state lúc nhấn Enter thì state có thể CHƯA cập nhật kịp -> token bị cắt cụt -> báo
  // "QR không hợp lệ". Đọc thẳng .value của DOM khi Enter mới lấy đủ chuỗi (kể cả URL đầy đủ).
  const deviceInputRef = useRef<HTMLInputElement | null>(null);
  const [serialBaud, setSerialBaud] = useState<number>(() => {
    try {
      return Number(localStorage.getItem(SERIAL_BAUD_PREF_KEY)) || 9600;
    } catch {
      return 9600;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SERIAL_BAUD_PREF_KEY, String(serialBaud));
    } catch {
      /* bỏ qua khi trình duyệt chặn localStorage */
    }
  }, [serialBaud]);
  const [deviceScanOn, setDeviceScanOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DEVICE_SCAN_PREF_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const [deviceMode, setDeviceMode] = useState<DeviceScanMode>(() => {
    try {
      const saved = localStorage.getItem(DEVICE_MODE_PREF_KEY);
      if (saved === 'serial' || saved === 'keyboard') return saved;
    } catch {
      /* bỏ qua */
    }
    // Mặc định ưu tiên CỔNG COM: đọc theo luồng byte, KHÔNG bị hệ điều hành nuốt/gộp phím lặp như
    // kiểu bàn phím USB (nguyên nhân token đọc thiếu 1 ký tự ở giữa với mã dài). Chỉ về bàn phím
    // khi trình duyệt không hỗ trợ Web Serial.
    return typeof navigator !== 'undefined' && 'serial' in navigator ? 'serial' : 'keyboard';
  });
  useEffect(() => {
    try {
      localStorage.setItem(DEVICE_SCAN_PREF_KEY, deviceScanOn ? '1' : '0');
    } catch {
      /* bỏ qua khi trình duyệt chặn localStorage */
    }
  }, [deviceScanOn]);
  useEffect(() => {
    try {
      localStorage.setItem(DEVICE_MODE_PREF_KEY, deviceMode);
    } catch {
      /* bỏ qua khi trình duyệt chặn localStorage */
    }
  }, [deviceMode]);

  const batchSeqRef = useRef(0);
  const flushedKeysRef = useRef<Set<string>>(new Set());

  // Camera quét liên tục (dí QR là quét luôn) — độc lập với luồng "chọn ảnh".
  const [cameraOn, setCameraOn] = useState(false);
  const cameraRef = useRef<Html5Qrcode | null>(null);
  // Promise resolve khi camera của lần bật trước đã giải phóng hẳn — lần bật sau chờ mốc này.
  const cameraReleasedRef = useRef<Promise<void>>(Promise.resolve());
  // Chống xử lý lặp CÙNG 1 mã trong các khung hình liên tiếp: mã -> mốc thời gian xử lý gần nhất.
  const scanCooldownRef = useRef<Map<string, number>>(new Map());
  // Bản sao rows để merge/đối chiếu trùng ngoài updater (tránh stale closure khi quét dồn dập).
  const rowsRef = useRef<ScanRow[]>([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  // Tập khoá "ký hiệu-số vé" đã có trong danh sách — tra trùng O(1), KHÔNG dựng lại từ toàn bộ rows
  // mỗi lượt quét. Cập nhật đồng bộ ngay khi thêm dòng để mẻ kế tiếp trong cùng vòng xử lý thấy được.
  const existingKeysRef = useRef<Set<string>>(new Set());
  // HÀNG ĐỢI quét: mọi lượt (camera / thiết bị / ảnh) đẩy token vào đây; 1 worker rút ra xử lý theo
  // mẻ. Nhờ vậy quét dồn dập KHÔNG BỊ RƠI lượt nào (trước đây lượt đến lúc đang bận bị bỏ qua).
  const scanQueueRef = useRef<string[]>([]);
  const processingRef = useRef(false);
  const [queuedCount, setQueuedCount] = useState(0);
  // Phiên hiện tại ở dạng ref (cập nhật ĐỒNG BỘ khi mở phiên) — để các mẻ liên tiếp trong cùng vòng
  // xử lý không mở lại phiên khi state `session` chưa kịp flush (quét dồn ở chế độ "chờ vé đầu tiên").
  const sessionRef = useRef<any>(null);
  useEffect(() => { sessionRef.current = session; }, [session]);

  useEffect(() => {
    loadAgentsWithCache().then(setAgents);
    getIssuancePlans({ pageIndex: 1, pageSize: 200 }).then((r) => setPlans(getItems(r) as IssuancePlan[])).catch(() => undefined);
  }, []);

  // Poll trạng thái phiên (tiến độ hàng đợi) khi phiên còn "Đang quét".
  useEffect(() => {
    if (!session?.id || session.status !== 1) return;
    const t = setInterval(async () => {
      try {
        const s = unwrap(await getTicketScanSessionApi(session.id));
        setSession((prev: any) => (prev ? { ...prev, ...s } : s));
      } catch { /* bỏ qua lỗi poll */ }
    }, 3000);
    return () => clearInterval(t);
  }, [session?.id, session?.status]);

  const resetSessionState = () => {
    setRows([]);
    rowsRef.current = [];
    batchSeqRef.current = 0;
    flushedKeysRef.current = new Set();
    existingKeysRef.current = new Set();
    scanQueueRef.current = [];
    processingRef.current = false;
    scanCooldownRef.current.clear();
    sessionRef.current = null;
    setQueuedCount(0);
  };

  // Thu hồi KHÔNG chọn trước đại lý: đại lý bị thu hồi lấy theo vé quét đầu tiên (server tra đại lý
  // đang được giao dải chứa vé đó). Tập dải hợp lệ server trả về chỉ gồm dải của đại lý này nên mọi vé
  // quét sau thuộc đại lý khác đều bị loại khi đối chiếu.
  const isAgentFromFirstTicket = scanType === 2 && !agentId;

  const getSessionAgentLabel = (s: any): string => {
    if (!s?.agentId) return '';
    const agent = agents.find((a) => String(a.id) === String(s.agentId));
    return agent ? `${agent.code} - ${agent.name}` : `#${s.agentId}`;
  };

  const openSessionForPlan = async (
    issuancePlanId: number,
    anchor?: { symbol: string; ticketNumber: string },
  ) => {
    const s = unwrap(await openTicketScanSessionApi({
      scanType,
      issuancePlanId,
      ...(agentId
        ? { agentId: Number(agentId) }
        : { anchorSymbol: anchor?.symbol, anchorTicketNumber: anchor?.ticketNumber }),
    }));
    sessionRef.current = s; // đồng bộ ngay, không chờ React flush
    setSession(s);
    setRanges(s.ranges ?? []);
    setPreSession(false);
    return s;
  };

  const handleOpenSession = async () => {
    // Giao vé vẫn bắt buộc chọn đại lý nhận; thu hồi thì được bỏ trống (lấy theo vé đầu tiên).
    if (!agentId && scanType !== 2) return toast.warning(st('chon_dai_ly_message'));
    // "Mở khoá" Web Audio ngay trong thao tác bấm nút (user-gesture) để beep quét kêu được ngay.
    primeScanAudio();
    // Không chọn kỳ (hoặc thu hồi chưa chọn đại lý): vào chế độ chờ — kỳ + đại lý sẽ tự lấy theo vé
    // quét đầu tiên (thông tin kỳ đã có trong vé, đại lý tra theo dải đã giao chứa vé).
    if (!planId || isAgentFromFirstTicket) {
      resetSessionState();
      setPreSession(true);
      toast.info(isAgentFromFirstTicket ? st('quet_ve_dau_lay_dai_ly_thu_hoi_message') : st('chua_chon_ky_quet_ve_dau_message'));
      return;
    }
    setOpening(true);
    try {
      resetSessionState();
      const s = await openSessionForPlan(Number(planId));
      toast.success(st('da_mo_phien_quet_session_code_message', { session_code: s.sessionCode }));
    } catch (e: any) {
      toast.error(e?.message || st('khong_mo_duoc_phien_quet_message'));
    } finally {
      setOpening(false);
    }
  };

  // Đối chiếu 1 vé với tập dải (nhận ranges tường minh để dùng ngay sau khi mở phiên, không chờ state).
  const checkRange = (rangeSet: ScanRange[], symbol: string, num: number): { ok: boolean; reason?: string } => {
    if (!rangeSet.length) return { ok: false, reason: st('phien_chua_co_tap_dai_hop_text') };
    const hit = rangeSet.some((r) => r.symbol === symbol && num >= r.start && num <= r.end);
    if (hit) return { ok: true };
    // Thu hồi: tập dải = dải đã giao cho đại lý của phiên -> vé ngoài tập này là vé của đại lý khác
    // (hoặc chưa giao / đã thu hồi) — nêu rõ tên đại lý đang thu hồi.
    if (scanType === 2) {
      const agentLabel = getSessionAgentLabel(sessionRef.current);
      if (agentLabel) return { ok: false, reason: st('ve_khong_thuoc_dai_ly_thu_hoi_text', { agent: agentLabel }) };
    }
    return { ok: false, reason: cfg.invalidRangeReason };
  };

  // Giải mã 1 lô nội dung QR (từ ảnh HOẶC camera) — GIẢI MÃ MỌI ĐỊNH DẠNG token (ECDSA/JWT offline,
  // fallback backend cho AES-256-GCM cũ). Chưa chọn kỳ -> mở phiên theo kỳ của vé HỢP LỆ ĐẦU TIÊN.
  // Vé ĐÃ CÓ trong danh sách (trùng ký hiệu + số vé) -> BỎ QUA, báo "đã tồn tại".
  const ingestScannedTokens = useCallback(
    async (
      rawScans: string[],
      opts?: { beep?: boolean },
    ): Promise<{ added: number; dup: number; bad: number }> => {
      const scans = rawScans.map((t) => (t || '').trim()).filter(Boolean);
      if (scans.length === 0 || (!sessionRef.current && !preSession)) return { added: 0, dup: 0, bad: 0 };

      setDecoding(true);
      try {
        const decodedList = await Promise.all(
          scans.map(async (raw) => ({ raw, d: await decodeScannedTicket(raw, decodeTicketToken) })),
        );

        // Chưa có phiên: mở phiên theo kỳ của vé hợp lệ đầu tiên. Đọc từ sessionRef để mẻ sau thấy
        // ngay phiên do mẻ trước vừa mở (state `session` có thể chưa flush kịp khi quét dồn dập).
        let activeSession = sessionRef.current;
        let activeRanges = activeSession?.ranges ?? ranges;
        if (!activeSession) {
          const firstOk = decodedList.find((x) => x.d);
          if (!firstOk?.d) {
            toast.error(st('khong_doc_duoc_ma_qr_nao_message'));
            return { added: 0, dup: 0, bad: decodedList.length };
          }
          // Đã chọn kỳ nhưng vé đầu tiên thuộc kỳ khác -> không mở phiên theo vé này.
          if (planId && String(firstOk.d.lotteryPeriodId) !== String(planId)) {
            toast.error(st('ve_thuoc_ky_phat_hanh_khac_text'));
            return { added: 0, dup: 0, bad: 0 };
          }
          if (openingSessionRef.current) return { added: 0, dup: 0, bad: 0 };
          openingSessionRef.current = true;
          try {
            activeSession = await openSessionForPlan(firstOk.d.lotteryPeriodId, {
              symbol: firstOk.d.symbol,
              ticketNumber: firstOk.d.ticketNumber,
            });
            activeRanges = activeSession.ranges ?? [];
            const matchedPlan = plans.find((p) => String(p.id) === String(firstOk.d!.lotteryPeriodId));
            toast.success(
              st('da_mo_phien_session_code_ky_message', { session_code: activeSession.sessionCode, value: matchedPlan?.planCode || `#${firstOk.d.lotteryPeriodId}` }),
            );
            if (isAgentFromFirstTicket) {
              toast.info(st('da_xac_dinh_dai_ly_thu_hoi_message', {
                agent: getSessionAgentLabel(activeSession) || `#${activeSession.agentId}`,
                ticket: `${firstOk.d.symbol}-${firstOk.d.ticketNumber}`,
              }));
            }
          } catch (e: any) {
            toast.error(e?.message || st('khong_mo_duoc_phien_quet_theo_message'));
            return { added: 0, dup: 0, bad: 0 };
          } finally {
            openingSessionRef.current = false;
          }
        }

        const sessionPlanId = Number(activeSession.issuancePlanId);
        const now = new Date().toISOString();
        const existingKeys = existingKeysRef.current; // tra trùng O(1), không dựng lại từ toàn bộ rows
        const seenInBatch = new Set<string>();        // chống trùng NGAY trong mẻ, tránh O(n²) .some()
        const baseSeq = rowsRef.current.length;       // số thứ tự chạy tiếp từ cuối danh sách hiện có
        const newRows: ScanRow[] = [];
        const dupLabels: string[] = [];
        let bad = 0;

        for (const { d } of decodedList) {
          if (!d || !d.symbol || !d.ticketNumber) {
            bad += 1;
            newRows.push({
              key: `bad-${existingKeys.size + newRows.length}-${Date.now()}`,
              seq: baseSeq + newRows.length + 1,
              symbol: '?', ticketNumber: '?', ok: false,
              reason: st('ma_qr_khong_dung_dinh_dang_text'), scannedAt: now,
            });
            continue;
          }
          const key = `${d.symbol}-${d.ticketNumber}`;
          if (existingKeys.has(key) || seenInBatch.has(key)) {
            dupLabels.push(key);
            continue;
          }
          seenInBatch.add(key);
          let res: { ok: boolean; reason?: string };
          if (d.lotteryPeriodId !== sessionPlanId) {
            res = { ok: false, reason: st('ve_thuoc_ky_phat_hanh_khac_text') };
          } else {
            res = checkRange(activeRanges, d.symbol, Number(d.ticketNumber));
          }
          newRows.push({
            key,
            seq: baseSeq + newRows.length + 1,
            symbol: d.symbol, ticketNumber: d.ticketNumber, ok: res.ok, reason: res.reason, scannedAt: now,
          });
        }

        if (newRows.length > 0) {
          for (const n of newRows) existingKeys.add(n.key);
          // Cập nhật rowsRef NGAY (không chờ React flush) để mẻ kế tiếp trong cùng vòng xử lý đối chiếu đúng.
          rowsRef.current = [...rowsRef.current, ...newRows];
          setRows(rowsRef.current);
        }

        if (dupLabels.length === 1) toast.info(st('ve_value_da_co_trong_danh_message', { value: dupLabels[0] }));
        else if (dupLabels.length > 1) toast.info(st('length_ve_da_co_trong_danh_message', { length: dupLabels.length }));

        // Beep tức thì cho luồng quét bằng máy/camera (opts.beep) — nhân viên quầy cần phản hồi
        // xanh/đỏ ngay để giữ nhịp, không chờ toast. Ưu tiên: có vé hợp lệ -> beep OK; toàn bộ bị
        // loại -> beep lỗi; chỉ toàn vé trùng -> beep trung tính.
        if (opts?.beep) {
          if (newRows.some((n) => n.ok)) beepOk();
          else if (newRows.length > 0) beepReject();
          else if (dupLabels.length > 0) beepDuplicate();
        }

        const validAdded = newRows.filter((n) => n.ok).length;
        if (validAdded > 0 && scans.length > 1) toast.success(st('da_them_valid_added_ve_hop_message', { valid_added: validAdded }));

        return { added: newRows.length, dup: dupLabels.length, bad };
      } catch (e: any) {
        toast.error(e?.message || st('giai_ma_qr_that_bai_message'));
        return { added: 0, dup: 0, bad: 0 };
      } finally {
        setDecoding(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preSession, ranges, plans, scanType, agentId, planId, agents, cfg.invalidRangeReason],
  );

  // Giữ tham chiếu mới nhất để callback camera / worker hàng đợi (stable) luôn gọi đúng bản.
  const ingestRef = useRef(ingestScannedTokens);
  useEffect(() => { ingestRef.current = ingestScannedTokens; }, [ingestScannedTokens]);

  // Worker rút HÀNG ĐỢI quét: mỗi vòng gom TẤT CẢ token đang chờ thành 1 mẻ rồi ingest. Chỉ 1 worker
  // chạy tại 1 thời điểm; lượt quét đến khi đang bận sẽ nằm trong hàng đợi và được mẻ kế tiếp xử lý
  // — không lượt nào bị bỏ.
  const drainScanQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (scanQueueRef.current.length > 0) {
        const batch = scanQueueRef.current.splice(0, scanQueueRef.current.length);
        setQueuedCount(scanQueueRef.current.length);
        await ingestRef.current(batch);
      }
    } finally {
      processingRef.current = false;
      setQueuedCount(scanQueueRef.current.length);
    }
  }, []);

  // Dọn bớt map cooldown khi vượt ngưỡng — bỏ các entry đã quá hạn cooldown (không còn tác dụng).
  const pruneScanCooldown = (nowMs: number) => {
    const map = scanCooldownRef.current;
    if (map.size <= SCAN_COOLDOWN_MAX_ENTRIES) return;
    map.forEach((ts, code) => {
      if (nowMs - ts > SCAN_COOLDOWN_MS) map.delete(code);
    });
  };

  // Đưa 1 lô token vào hàng đợi rồi kích worker (dùng chung cho ảnh / camera / thiết bị).
  const enqueueScans = useCallback((texts: string[]) => {
    const clean = texts.map((t) => (t || '').trim()).filter(Boolean);
    if (clean.length === 0) return;
    scanQueueRef.current.push(...clean);
    setQueuedCount(scanQueueRef.current.length);
    void drainScanQueue();
  }, [drainScanQueue]);

  // Đọc QR từ 1 tập ảnh → đẩy vào cùng hàng đợi với camera / thiết bị quét.
  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || (!session && !preSession)) return;
    setDecoding(true);
    try {
      const reader = new Html5Qrcode(readerElementId, { verbose: false } as any);
      const tokens: string[] = [];
      for (const file of Array.from(fileList)) {
        try {
          const decoded = await reader.scanFile(file, false);
          if (decoded) tokens.push(decoded.trim());
        } catch {
          toast.error(st('khong_doc_duoc_ma_qr_tu_message', { name: file.name }));
        }
      }
      enqueueScans(tokens);
    } finally {
      setDecoding(false);
    }
  };

  // Nhận 1 nội dung mã đã đọc được — từ CAMERA hoặc THIẾT BỊ QUÉT cầm tay: chặn đọc LẶP cùng 1 mã
  // (cooldown) rồi ĐẨY VÀO HÀNG ĐỢI (không xử lý trực tiếp → không rơi lượt). cooldownMs khác nhau:
  // camera cần dài (khung lặp), thiết bị quét chỉ cần ngắn (1 lần bóp cò = 1 sự kiện).
  const handleDecodedText = useCallback((decodedText: string, cooldownMs: number = SCAN_COOLDOWN_MS) => {
    const text = (decodedText || '').trim();
    if (!text) return;
    const nowMs = Date.now();
    if (nowMs - (scanCooldownRef.current.get(text) || 0) < cooldownMs) return;
    scanCooldownRef.current.set(text, nowMs);
    pruneScanCooldown(nowMs);
    enqueueScans([text]);
  }, [enqueueScans]);

  // Kiểu "bàn phím USB (HID)": máy quét "gõ" nội dung mã QR (thường là URL đầy đủ in trên vé) vào ô
  // nhập rồi tự bấm Enter/Tab. Ô nhập KHÔNG kiểm soát bằng state.
  //
  // KHÔNG chỉ dựa vào phím Enter: máy quét gõ cực nhanh, phím kết thúc có thể tới TRƯỚC khi ký tự
  // cuối kịp vào .value -> chuỗi bị cắt cụt -> không đọc được token. Cách chắc chắn: mỗi lần .value
  // đổi thì hẹn lại giờ; chỉ khi NGỪNG nhập một nhịp (DEVICE_INPUT_DEBOUNCE_MS) mới lấy .value —
  // lúc này .value chắc chắn đã đủ. Phím Enter/Tab (nếu có) chỉ để gửi NGAY, không bắt buộc.
  const deviceInputTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const submitDeviceInput = useCallback(() => {
    if (deviceInputTimerRef.current) {
      clearTimeout(deviceInputTimerRef.current);
      deviceInputTimerRef.current = null;
    }
    const el = deviceInputRef.current;
    if (!el) return;
    const raw = el.value.replace(/[\r\n\t]+/g, '').trim();
    el.value = '';
    if (raw) handleDecodedText(raw, DEVICE_COOLDOWN_MS);
  }, [handleDecodedText]);

  const handleDeviceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Máy quét gắn hậu tố xuống dòng/tab -> gửi ngay, không chờ debounce.
    if (/[\r\n\t]/.test(event.target.value)) {
      submitDeviceInput();
      return;
    }
    if (deviceInputTimerRef.current) clearTimeout(deviceInputTimerRef.current);
    deviceInputTimerRef.current = setTimeout(submitDeviceInput, DEVICE_INPUT_DEBOUNCE_MS);
  };

  const handleDeviceKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' && event.key !== 'Tab') return;
    event.preventDefault();
    submitDeviceInput();
  };

  // Dọn hẹn giờ khi rời kiểu bàn phím / unmount.
  useEffect(() => () => {
    if (deviceInputTimerRef.current) clearTimeout(deviceInputTimerRef.current);
  }, []);

  // Kiểu "cổng COM (USB Serial)": đọc trực tiếp từ cổng nối tiếp qua Web Serial API. useSerialScanner
  // chỉ phát MỘT chuỗi hoàn chỉnh cho mỗi lần quét (đã tách theo hậu tố, không bị pause cắt đôi).
  const serialScanner = useSerialScanner({
    baudRate: serialBaud,
    onScan: (code: string) => handleDecodedText(code, DEVICE_COOLDOWN_MS),
    onError: (message) => toast.error(message),
  });

  // Tự đưa con trỏ về ô "thiết bị quét" (chỉ kiểu bàn phím) mỗi khi sẵn sàng để nhân viên quét liên
  // tục không phải bấm chuột. Không cướp focus nếu người dùng đang gõ ở ô nhập khác (VD: ô ghi chú).
  useEffect(() => {
    const canScan =
      deviceScanOn &&
      deviceMode === 'keyboard' &&
      (preSession || (!!session && session.status !== 3));
    if (!canScan) return;
    const active = document.activeElement;
    const typingElsewhere =
      active instanceof HTMLElement &&
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName) &&
      active !== deviceInputRef.current;
    if (!typingElsewhere) deviceInputRef.current?.focus();
  }, [deviceScanOn, deviceMode, decoding, preSession, session]);

  // Ngắt cổng COM khi tắt thiết bị quét, đổi sang kiểu bàn phím, hoặc thoát/kết thúc phiên.
  const sessionClosed = session?.status === 3 || (!session && !preSession);
  useEffect(() => {
    if ((!deviceScanOn || deviceMode !== 'serial' || sessionClosed) && serialScanner.status === 'connected') {
      void serialScanner.disconnect();
    }
    // serialScanner đổi tham chiếu mỗi lần render — chỉ phụ thuộc vào .status và .disconnect (ổn định).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceScanOn, deviceMode, sessionClosed, serialScanner.status, serialScanner.disconnect]);

  // Bật/tắt camera. Element camera luôn nằm trong DOM (ẩn bằng CSS) để html5-qrcode đo được khung.
  useEffect(() => {
    if (!cameraOn) return;
    let cancelled = false;
    const qr = new Html5Qrcode(cameraElementId, { verbose: false } as any);
    cameraRef.current = qr;
    // Xếp hàng sau lần tắt camera trước (cameraReleasedRef) — bật/tắt nhanh lúc trình duyệt còn hỏi quyền
    // không bị 2 instance tranh camera.
    const startPromise = cameraReleasedRef.current.then(() => {
      if (cancelled) return undefined;
      return qr.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        // Bọc để KHÔNG nhận nhầm tham số thứ 2 (Html5QrcodeResult) làm cooldownMs — camera dùng cooldown mặc định (dài).
        (decodedText: string) => handleDecodedText(decodedText),
        () => { /* không log mỗi khung không có QR */ },
      );
    });
    startPromise
      .catch((e: any) => {
        if (cancelled) return;
        cameraRef.current = null;
        setCameraOn(false);
        toast.error(
          e?.message ||
            st('khong_mo_duoc_camera_kiem_tra_message'),
        );
      });
    return () => {
      cancelled = true;
      cameraRef.current = null;
      // stop() của html5-qrcode throw đồng bộ nếu camera chưa start xong (đang chờ cấp quyền) — xem
      // helpers/html5QrcodeCamera.ts. Chờ start kết thúc rồi mới dừng, không bao giờ throw.
      cameraReleasedRef.current = stopHtml5QrcodeSafely(qr, startPromise);
    };
  }, [cameraOn, cameraElementId, handleDecodedText]);

  // Tắt camera khi phiên đã xác nhận, hoặc khi thoát hẳn (không phiên và không ở chế độ chờ vé đầu tiên).
  useEffect(() => {
    if (session?.status === 3 || (!session && !preSession)) setCameraOn(false);
  }, [session, preSession]);

  const pendingValid = useMemo(
    () => rows.filter((r) => r.ok && !flushedKeysRef.current.has(r.key)),
    [rows],
  );

  // Chỉ render tối đa MAX_VISIBLE_ROWS dòng GẦN NHẤT (cuối mảng) để bảng không giật ở phiên nghìn vé.
  const visibleRows = useMemo(
    () => (rows.length > MAX_VISIBLE_ROWS ? rows.slice(-MAX_VISIBLE_ROWS) : rows),
    [rows],
  );
  const hiddenRowCount = rows.length - visibleRows.length;

  const handleFlush = async () => {
    if (!session || pendingValid.length === 0) return;
    setFlushing(true);
    try {
      batchSeqRef.current += 1;
      await ingestTicketScanEntriesApi(session.id, {
        clientBatchId: `${session.id}-${batchSeqRef.current}-${Date.now()}`,
        clientBatchSeq: batchSeqRef.current,
        entries: pendingValid.map((r) => ({
          ticketNumber: r.ticketNumber,
          symbol: r.symbol,
          matchStatus: 1, // Valid — server vẫn tự re-validate theo tập dải của phiên
          scannedAt: r.scannedAt,
        })),
      });
      pendingValid.forEach((r) => flushedKeysRef.current.add(r.key));
      toast.success(st('da_day_length_ve_vao_hang_message', { length: pendingValid.length }));
      setRows((prev) => [...prev]); // ép re-render để cập nhật pendingValid
    } catch (e: any) {
      toast.error(e?.message || st('day_hang_doi_that_bai_message'));
    } finally {
      setFlushing(false);
    }
  };

  const handleFinalize = async () => {
    if (!session) return;
    if (pendingValid.length > 0) {
      toast.warning(st('con_ve_chua_day_vao_hang_message'));
      return;
    }
    setFinalizing(true);
    try {
      const s = unwrap(await finalizeTicketScanSessionApi(session.id, note || undefined));
      setSession(s);
      if (s.status === 3) {
        toast.success(`${cfg.successPrefix} ${s.resultDocumentCode || ''}`.trim());
      } else if (s.finalizeError) {
        toast.error(s.finalizeError);
      }
    } catch (e: any) {
      toast.error(e?.message || cfg.confirmFailMsg);
    } finally {
      setFinalizing(false);
    }
  };

  const handleCancel = async () => {
    if (!session) return;
    try {
      await cancelTicketScanSessionApi(session.id);
      toast.info(st('da_huy_phien_quet_message'));
      setSession(null);
      setPreSession(false);
      setRanges([]);
      resetSessionState();
    } catch (e: any) {
      toast.error(e?.message || st('huy_phien_that_bai_message'));
    }
  };

  const isScanning = session?.status === 1;
  const isFinalized = session?.status === 3;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={cfg.title} pageTitle={cfg.breadcrumbParent} />
          <div id={readerElementId} style={{ display: 'none' }} />

          {!session && !preSession ? (
            <Card>
              <CardHeader><h5 className="card-title mb-0">{cfg.openTitle}</h5></CardHeader>
              <CardBody>
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Label className="form-label">
                      {cfg.agentLabel}{' '}
                      {scanType === 2
                        ? <span className="text-muted">{st('tuy_chon_text')}</span>
                        : <span className="text-danger">*</span>}
                    </Label>
                    <Input type="select" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                      <option value="">{scanType === 2 ? st('bo_trong_lay_dai_ly_theo_ve_dau_option') : st('chon_dai_ly_option')}</option>
                      {agents.map((a) => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Label className="form-label">{st('ke_hoach_phat_hanh_label')} <span className="text-muted">{st('tuy_chon_text')}</span></Label>
                    <Input type="select" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                      <option value="">{st('bo_trong_lay_theo_ve_dau_option')}</option>
                      {plans.map((p) => <option key={p.id} value={p.id}>{p.planCode}</option>)}
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Button color="primary" disabled={opening} onClick={handleOpenSession}>
                      {opening ? <Spinner size="sm" /> : <i className="ri-qr-scan-2-line align-bottom me-1" />}
                      {st('bat_dau_quet_button')}
                    </Button>
                  </Col>
                </Row>
                <p className="text-muted small mb-0 mt-2">
                  {st('thong_tin_ky_phat_hanh_da_text')}
                </p>
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="card-title mb-0">
                  {session ? (
                    <>
                      {st('phien_text')} {session.sessionCode}
                      <span className={`badge ms-2 bg-${isFinalized ? 'success' : isScanning ? 'info' : 'secondary'}-subtle text-${isFinalized ? 'success' : isScanning ? 'info' : 'secondary'}`}>
                        {session.statusName}
                      </span>
                    </>
                  ) : (
                    <>{st('cho_ve_dau_tien_text')} <span className="badge ms-2 bg-warning-subtle text-warning">{st('chua_mo_phien_text')}</span></>
                  )}
                </h5>
                <div className="d-flex gap-2">
                  {!session && (
                    <Button color="light" size="sm" onClick={() => { setPreSession(false); }}>{st('quay_lai_button')}</Button>
                  )}
                  {session && !isFinalized && <Button color="light" size="sm" onClick={handleCancel}>{st('huy_phien_button')}</Button>}
                  {isFinalized && <Button color="primary" size="sm" onClick={() => { setSession(null); setPreSession(false); }}>{st('phien_moi_button')}</Button>}
                </div>
              </CardHeader>
              <CardBody>
                {!session && (
                  <div className="alert alert-info py-2">
                    <i className="ri-information-line me-1" />
                    {isAgentFromFirstTicket ? st('cho_ve_dau_lay_dai_ly_thu_hoi_text') : st('chua_chon_ky_quet_ve_dau_text')}
                  </div>
                )}
                {session && scanType === 2 && (
                  <div className="alert alert-secondary py-2">
                    <i className="ri-store-2-line me-1" />
                    {st('dai_ly_thu_hoi_cua_phien_text')} <b>{getSessionAgentLabel(session)}</b>
                    {isAgentFromFirstTicket && <span className="text-muted ms-1">{st('xac_dinh_theo_ve_dau_tien_text')}</span>}
                  </div>
                )}
                {session && (
                  <Row className="text-center mb-3">
                    <Col><small className="text-muted d-block">{st('da_quet_hint')}</small><b>{session.scannedCount ?? 0}</b></Col>
                    <Col><small className="text-muted d-block">{st('hop_le_text')}</small><b className="text-success">{session.validCount ?? 0}</b></Col>
                    <Col><small className="text-muted d-block">{st('bi_loai_hint')}</small><b className="text-danger">{session.rejectedCount ?? 0}</b></Col>
                    <Col><small className="text-muted d-block">{st('trung_hint')}</small><b>{session.duplicateCount ?? 0}</b></Col>
                    <Col><small className="text-muted d-block">{st('hang_doi_hint')}</small><b>{session.lastProcessedBatchSeq ?? 0}/{session.lastClientBatchSeq ?? 0}</b></Col>
                  </Row>
                )}

                {session?.finalizeError && (
                  <div className="alert alert-danger py-2">{session.finalizeError}</div>
                )}

                {!isFinalized && (
                  <>
                    {/* Khu vực chọn NGUỒN QUÉT — 3 thẻ ngang hàng: Camera / Ảnh vé / Thiết bị quét. */}
                    <div className="border rounded p-3 mb-3 bg-light-subtle">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <i className="ri-focus-2-line fs-18 text-primary" />
                        <h6 className="mb-0">{st('nguon_quet_ve_title')}</h6>
                        <span className="text-muted fs-12">{st('co_the_dung_dong_thoi_nhieu_text')}</span>
                        {(decoding || queuedCount > 0) && (
                          <span className="badge bg-info-subtle text-info ms-auto d-inline-flex align-items-center gap-1">
                            <Spinner size="sm" style={{ width: 11, height: 11 }} />
                            {st('dang_xu_ly_text')}{queuedCount > 0 ? st('con_queued_count_text', { queued_count: queuedCount }) : ''}
                          </span>
                        )}
                      </div>

                      <Row className="g-3">
                        {/* Camera */}
                        <Col lg={4}>
                          <div className="h-100 border rounded p-3 bg-body">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="fw-medium">
                                <i className="ri-camera-line me-1 text-muted" />{st('camera_text')}
                              </span>
                              <div className="form-check form-switch mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="scan-src-camera"
                                  checked={cameraOn}
                                  onChange={() => setCameraOn((v) => !v)}
                                />
                              </div>
                            </div>
                            <p className="text-muted fs-12 mb-0">
                              {st('bat_camera_roi_di_ma_qr_text')}
                            </p>
                          </div>
                        </Col>

                        {/* Ảnh vé */}
                        <Col lg={4}>
                          <div className="h-100 border rounded p-3 bg-body">
                            <div className="fw-medium mb-2">
                              <i className="ri-image-line me-1 text-muted" />{st('anh_ve_text')}
                            </div>
                            <Label className="btn btn-outline-primary btn-sm w-100 mb-2">
                              <i className="ri-image-add-line align-bottom me-1" />
                              {decoding ? st('dang_doc_ma_text') : st('chon_chup_anh_ve_text')}
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                capture="environment"
                                hidden
                                disabled={decoding}
                                onChange={(e) => { handleFiles(e.target.files); e.currentTarget.value = ''; }}
                              />
                            </Label>
                            <p className="text-muted fs-12 mb-0">{st('chon_nhieu_anh_cung_luc_cung_text')}</p>
                          </div>
                        </Col>

                        {/* Thiết bị quét cầm tay (Honeywell...) — bàn phím USB (HID) / cổng COM (Serial). */}
                        <Col lg={4}>
                          <div className="h-100 border rounded p-3 bg-body">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="fw-medium">
                                <i className="ri-barcode-box-line me-1 text-muted" />{st('thiet_bi_quet_text')}
                              </span>
                              <div className="form-check form-switch mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="scan-src-device"
                                  checked={deviceScanOn}
                                  onChange={() => setDeviceScanOn((v) => !v)}
                                />
                              </div>
                            </div>

                            {!deviceScanOn ? (
                              <p className="text-muted fs-12 mb-0">
                                {st('bat_de_dung_may_quet_honeywell_text')}
                              </p>
                            ) : (
                              <>
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  className="mb-2"
                                  value={deviceMode}
                                  onChange={(e) => setDeviceMode(e.target.value === 'serial' ? 'serial' : 'keyboard')}
                                >
                                  <option value="keyboard">{st('ban_phim_usb_hid_option')}</option>
                                  <option value="serial">{st('cong_com_usb_serial_option')}</option>
                                </Input>

                                {deviceMode === 'keyboard' && (
                                  <>
                                    <div className="position-relative">
                                      {/* Uncontrolled — đọc .value trực tiếp (xem submitDeviceInput). */}
                                      <Input
                                        innerRef={deviceInputRef}
                                        type="text"
                                        bsSize="sm"
                                        autoComplete="off"
                                        placeholder={st('di_may_quet_vao_day_tu_placeholder')}
                                        onChange={handleDeviceChange}
                                        onKeyDown={handleDeviceKeyDown}
                                      />
                                      {decoding && (
                                        <span className="position-absolute end-0 top-50 translate-middle-y me-2">
                                          <Spinner size="sm" color="primary" />
                                        </span>
                                      )}
                                    </div>
                                    {serialScanner.supported && (
                                      <p className="text-warning fs-12 mb-0 mt-1">
                                        <i className="ri-error-warning-line me-1" />
                                        {st('kieu_ban_phim_co_the_nuot_text')} <b>{st('cong_com_text')}</b>{st('hoac_chinh_may_quet_them_text')} <b>{st('inter_character_delay_5ms_text')}</b>.
                                      </p>
                                    )}
                                  </>
                                )}

                                {deviceMode === 'serial' && (
                                  !serialScanner.supported ? (
                                    <p className="text-danger fs-12 mb-0">
                                      {st('trinh_duyet_khong_ho_tro_web_text')}
                                    </p>
                                  ) : serialScanner.status === 'connected' ? (
                                    <>
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="badge bg-success-subtle text-success text-truncate">
                                          <i className="ri-plug-line me-1" />
                                          {serialScanner.portLabel || st('da_ket_noi_text')} · {serialBaud} {st('baud_text')}
                                        </span>
                                        <Button color="danger" outline size="sm" onClick={() => serialScanner.disconnect()}>
                                          {st('ngat_button')}
                                        </Button>
                                        {decoding && <Spinner size="sm" color="primary" />}
                                      </div>
                                      <p className="text-muted fs-12 mb-0 mt-1">
                                        {st('da_nhan_text')} <b>{serialScanner.rxBytes}</b> {st('byte_text')} <b>{serialScanner.rxCount}</b> {st('lan_quet_text')}
                                        {serialScanner.lastRx && <> {st('gan_nhat_text')} <code className="text-body">{serialScanner.lastRx}</code></>}
                                      </p>
                                      {serialScanner.rxBytes === 0 && (
                                        <p className="text-warning fs-12 mb-0">
                                          <i className="ri-error-warning-line me-1" />
                                          {st('quet_ma_text')} <b>{st('byte_0_text')}</b>{st('may_quet_chua_gui_gi_qua_text')} <b>{st('usb_serial_com_text')}</b> {st('khong_phai_hid_keyboard_2_da_text')} <b>{st('dung_cong_text')}</b> {st('cua_may_quet_o_hop_thoai_text')} <b>{st('truyen_tu_dong_sau_khi_quet_text')}</b>.
                                        </p>
                                      )}
                                      {serialScanner.rxBytes > 0 && serialScanner.rxCount === 0 && (
                                        <p className="text-warning fs-12 mb-0">
                                          <i className="ri-error-warning-line me-1" />
                                          {st('co_byte_ve_nhung_khong_boc_text')} <b>{st('sai_baud_text')}</b> {st('thu_baud_khac_ngat_roi_ket_text')}
                                        </p>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <Input
                                        type="select"
                                        bsSize="sm"
                                        className="mb-2"
                                        value={serialBaud}
                                        onChange={(e) => setSerialBaud(Number(e.target.value))}
                                      >
                                        {SERIAL_BAUD_OPTIONS.map((b) => (
                                          <option key={b} value={b}>{b} {st('baud_text')}</option>
                                        ))}
                                      </Input>
                                      <Button
                                        color="primary"
                                        outline
                                        size="sm"
                                        className="w-100"
                                        disabled={serialScanner.status === 'connecting'}
                                        onClick={() => serialScanner.connect()}
                                      >
                                        {serialScanner.status === 'connecting' ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          <>
                                            <i className="ri-plug-line align-bottom me-1" />
                                            {st('ket_noi_cong_com_text')}
                                          </>
                                        )}
                                      </Button>
                                    </>
                                  )
                                )}
                              </>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    {/* Thanh hành động: ghi chú + đẩy hàng đợi + xác nhận. */}
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                      <Input
                        type="text"
                        className="flex-grow-1"
                        style={{ minWidth: 220, maxWidth: 380 }}
                        placeholder={cfg.notePlaceholder}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                      <Button
                        color="light"
                        className="border"
                        disabled={!session || flushing || pendingValid.length === 0}
                        onClick={handleFlush}
                      >
                        {flushing ? <Spinner size="sm" /> : <i className="ri-upload-2-line align-bottom me-1" />}
                        {st('day_vao_hang_doi_button')}
                        {pendingValid.length > 0 && (
                          <span className="badge bg-secondary ms-1">{pendingValid.length}</span>
                        )}
                      </Button>
                      <Button color="success" disabled={!session || finalizing} onClick={handleFinalize}>
                        {finalizing ? <Spinner size="sm" /> : <i className="ri-check-double-line align-bottom me-1" />}
                        {cfg.confirmLabel}
                      </Button>
                    </div>

                    {/* Khung camera — luôn trong DOM để html5-qrcode đo được, ẩn bằng CSS khi tắt. */}
                    <div className={cameraOn ? 'text-center mb-3' : 'd-none'}>
                      <div
                        id={cameraElementId}
                        style={{ maxWidth: 360, margin: '0 auto', borderRadius: 8, overflow: 'hidden' }}
                      />
                      <p className="text-muted fs-12 mt-1 mb-0">
                        {st('di_ma_qr_tren_ve_vao_text')}
                      </p>
                    </div>
                  </>
                )}

                <DataTableShell
                  headerRow={
                    <tr>
                      <th style={{ width: 46 }} className="text-center">#</th>
                      <th>{st('ky_hieu_column')}</th><th>{st('so_ve_column')}</th><th>{st('ket_qua_doi_chieu_column')}</th><th>{st('thoi_diem_column')}</th>
                    </tr>
                  }
                >
                  {rows.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-muted py-4">{st('chua_quet_ve_nao_text')}</td></tr>
                  ) : (
                    <>
                      {hiddenRowCount > 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted fs-12 bg-light-subtle">
                            {st('da_an_text')} {hiddenRowCount} {st('ve_quet_truoc_do_chi_hien_text')} {MAX_VISIBLE_ROWS} {st('ve_gan_nhat_tong_text')} {rows.length})
                          </td>
                        </tr>
                      )}
                      {visibleRows.map((r) => (
                        <ScanResultRow
                          key={r.key}
                          index={r.seq}
                          row={r}
                          flushed={flushedKeysRef.current.has(r.key)}
                        />
                      ))}
                    </>
                  )}
                </DataTableShell>
              </CardBody>
            </Card>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default QrScanSessionPage;
